import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import AddRankingModal from "./AddRankingModal";
import {
  deleteRanking,
  fetchRankings,
  fetchRankingsTrends,
} from "../../services/rankingsApi";
import moment from "moment";
import { Box, CircularProgress, Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";

interface Props {
  isPrintMode: boolean;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
  clinicData: string;
}

const RankingsSection = ({ isPrintMode, dateFilter, clinicData }: Props) => {
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: number;
    type: "ranking";
  } | null>(null);
  const [mode, setMode] = useState<"add" | "edit">();
  const [data, setData] = useState<any[]>([]);
  const [trends, setTrends] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRanking, setSelectedRanking] = useState<any>(null);

  // Parse date to get month and year
  const parseDate = (date: string) => {
    const parsedDate = moment(date);
    return {
      month: parsedDate.month() + 1, // moment months are 0-based
      year: parsedDate.year(),
    };
  };

  const fetchRankingsData = async () => {
    setLoading(true);
    try {
      const endDate = parseDate(dateFilter.endDate);
      const [res1, res] = await Promise.all([
        fetchRankingsTrends(clinicData, endDate.year),
        fetchRankings(clinicData, endDate.month, endDate.year),
      ]);
      if (res?.ranking?.length > 0) {
        setData(res?.ranking);
      } else {
        setData([]);
      }
      if (res?.status?.msg === "Success") {
        setTrends(
          res1?.rankingTrends?.map((x: any) => ({
            ...x,
            ranking: Number(x?.avgCurrent),
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
    }
  };

  console.log(dateFilter,"dateFilter")

  // Fetch rankings data
  useEffect(() => {
    if (clinicData && dateFilter?.startDate && dateFilter?.endDate) {
      fetchRankingsData();
    }
  }, [dateFilter, clinicData]);

  const handleOpenModal = (mode: "add" | "edit") => {
    setMode(mode);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedRanking(null);
    setIsModalOpen(false);
  };

  const handleSaveRanking = (formData: any) => {
    fetchRankingsData();
    handleCloseModal();
  };

  const columns: GridColDef[] = [
    {
      field: "keyword",
      headerName: "Keyword",
      flex: 1,
      minWidth: 190, // Ensure minimum width for better readability
      renderCell: (params: GridRenderCellParams) => (
        <div
          style={{
            width: "100%",
            whiteSpace: "normal",
            lineHeight: "1.5",
            padding: "8px 0",
            wordBreak: "break-word",
            overflow: "visible",
            textOverflow: "clip",
          }}
        >
          {params.row.keyword}
        </div>
      ),
    },
    {
      field: "current",
      headerName: "Current",
      width: 70,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
    },
    {
      field: "previous",
      headerName: "Baseline",
      width: 80,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
    },
    {
      field: "change",
      headerName: "Change",
      width: 70,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      renderCell: (params) => {
        const current = parseFloat(params.row.current);
        const previous = parseFloat(params.row.previous);
        const change = (previous || 100) - current;
        const changeText =
          change > 0 ? `↑${change}` : change < 0 ? `↓${Math.abs(change)}` : "-";

        return (
          <span
            className={`font-medium ${
              change > 0
                ? "text-green-600"
                : change < 0
                ? "text-red-600"
                : "text-gray-600"
            }`}
          >
            {changeText}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      disableColumnMenu: true,
      filterable: false,
      hideSortIcons: true,
      renderCell: (params) => (
        <div className="flex justify-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRanking(params.row);
              handleOpenModal("edit");
            }}
            className="btn btn-sm btn-secondary"
            style={{
              padding: "2px 8px",
              marginTop: "10px",
              backgroundColor: "#4CC9C0",
              border: "none",
            }}
          >
            ✏️
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setItemToDelete(params.row);
              setDeleteDialogOpen(true);
            }}
            className="btn btn-sm"
            style={{
              padding: "2px 8px",
              marginTop: "10px",
              backgroundColor: "#E0585B",
              border: "none",
            }}
          >
            🗑️
          </button>
        </div>
      ),
    },
  ];

const LoadingSpinner = styled(CircularProgress)({
    color: "#4CC9C0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  });

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md h-[600px] relative">
        <h2 className="text-2xl font-bold text-[#194E5B] mb-4">SEO Rankings</h2>
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          style={{ alignItems: "start" }}
        >
          <div className="col-span-2">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }
  const StyledGridOverlay = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    "& .no-rows-primary": {
      fill: "#3D4751",
      ...theme.applyStyles("light", {
        fill: "#AEB8C2",
      }),
    },
    "& .no-rows-secondary": {
      fill: "#1D2126",
      ...theme.applyStyles("light", {
        fill: "#E8EAED",
      }),
    },
  }));

  function CustomNoRowsOverlay() {
    return (
      <StyledGridOverlay>
        <Box sx={{ mt: 2 }}>No Rankings Found</Box>
      </StyledGridOverlay>
    );
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const transformData = (apiData: any[]) => {
    const monthMap = new Map(
      apiData.map((item) => [parseInt(item.month), item])
    );
  
    return monthNames.map((name, index) => {
      const item = monthMap.get(index + 1); // month is 1-indexed
      return {
        name,
        top10: item ? parseInt(item.count_0_10) : 0,
        top20: item ? parseInt(item.count_11_20) : 0,
        top30: item ? parseInt(item.count_21_30) : 0,
        top50: item ? parseInt(item.count_31_50) : 0,
        top50plus: item ? parseInt(item.count_50_plus) : 0,
      };
    });
  };
  
  const handleDeleteConfirm = async (data: any) => {
    setIsLoading(true);
    console.log(data, "itemToDelete");
    // if (!data) return;

    try {
      const res = await deleteRanking(clinicData, data.id);
      if (res?.status?.msg === "Success") {
        fetchRankingsData();
        toast.success(`Successfully deleted ranking`);
        setIsLoading(false);
      }
    } catch (error) {
      toast.error(`Failed to delete ranking`);
      console.error(`Error deleting ranking:`, error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-[#194E5B] mb-4">SEO Rankings</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#194E5B]">
                Current Rankings
              </h3>
              {!isPrintMode && (
                <div className="flex gap-2">
                  <button
                    style={{ backgroundColor: "#0E3843" }}
                    className="btn btn-sm bg-[#0E3843] rounded-md text-white border border-blue-600 hover:bg-[#0A2A30]"
                    onClick={() => handleOpenModal("add")}
                  >
                    ➕ Add New Ranking
                  </button>
                </div>
              )}
            </div>
            <div style={{ width: "100%" }}>
              <DataGrid
                rows={data}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
                columns={columns}
                disableRowSelectionOnClick
                hideFooter
                autoHeight
                getRowHeight={() => "auto"}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal !important",
                    wordWrap: "break-word !important",
                    padding: "8px 16px",
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-row": {
                    maxHeight: "none !important",
                  },
                  "& .MuiDataGrid-cellContent": {
                    whiteSpace: "normal !important",
                    lineHeight: "1.5 !important",
                  },
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    border: "none",
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    display: "none",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "visible !important",
                  },
                }}
              />
            </div>
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="border-[#ccced1] p-4 rounded-lg border-1 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold text-[#0E3843]">
                  {data?.length}
                </div>
                <div className="text-[14px] font-medium">Keywords up</div>
              </div>
              <div className="border-[#ccced1] p-4 rounded-lg border-1 flex flex-col items-center justify-center">
                <div className="text-2xl font-medium text-[#4CC9C0]">
                  {data?.filter((x) => x?.current <= 3).length}
                  <span className="text-[#0E3843]">/{data?.length}</span>
                </div>
                <div className="text-[14px] font-medium ">In Top 3</div>
              </div>
              <div className="border-[#ccced1] p-4 rounded-lg border-1 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold text-[#4CC9C0]">
                  {data?.filter((x) => x?.current <= 10).length}
                  <span className="text-[#0E3843]">/{data?.length}</span>
                </div>
                <div className="text-[14px] font-medium">In Top 10</div>
              </div>
              <div className="border-[#ccced1] p-4 rounded-lg border-1 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold text-[#4CC9C0]">
                  {data?.filter((x) => x?.current <= 20).length}
                  <span className="text-[#0E3843]">/{data?.length}</span>
                </div>
                <div className="text-[14px] font-medium">In Top 20</div>
              </div>
              <div className="border-[#ccced1] p-4 rounded-lg border-1 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold text-[#4CC9C0]">
                  {data?.filter((x) => x?.current <= 30).length}
                  <span className="text-[#0E3843]">/{data?.length}</span>
                </div>
                <div className="text-[14px] font-medium">In Top 30</div>
              </div>
              <div className="border-[#ccced1] p-4 rounded-lg border-1 flex flex-col items-center justify-center">
                <div className="text-2xl font-bold text-[#4CC9C0]">
                  {data?.filter((x) => x?.current <= 100).length}
                  <span className="text-[#0E3843]">/{data?.length}</span>
                </div>
                <div className="text-[14px] font-medium">In Top 100</div>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-[#194E5B] my-4">
              Keyword Distribution by Ranking Tiers
            </h3>
            <div style={{ flex: 1, maxHeight: "300px", minHeight: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  width={500}
                  height={300}
                  data={transformData(trends)}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    label="Top 10"
                    dataKey="top10"
                    stackId="a"
                    fill="#8b5cf6"
                  />
                  <Bar
                    label="Top 20"
                    dataKey="top20"
                    stackId="a"
                    fill="#ef4444"
                  />
                  <Bar
                    label="Top 30"
                    dataKey="top30"
                    stackId="a"
                    fill="#f59e0b"
                  />
                  <Bar
                    label="Top 50"
                    dataKey="top50"
                    stackId="a"
                    fill="#3b82f6"
                  />
                  <Bar
                    label="Top 50+"
                    dataKey="top50plus"
                    stackId="a"
                    fill="#22c55e"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <AddRankingModal
          clinicData={clinicData}
          dateFilter={dateFilter}
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveRanking}
          mode={mode}
          initialData={selectedRanking}
        />
      </div>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="p-4">
          <h3
            className="text-lg font-medium text-gray-900"
            id="alert-dialog-title"
          >
            Delete {itemToDelete?.type}
          </h3>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this {itemToDelete?.type}? This
              action cannot be undone.
            </p>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
              onClick={() => handleDeleteConfirm(itemToDelete)}
            >
              {isLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default RankingsSection;
